import Config from "../../../config";
import store from "../../../store";
import {getCurrentLayout} from "../../appLayout";
import {getBvGroupFeatures} from "../../../api/server-api";
import {checkIfLookFeaturesReceived} from "../../../api/look-api";
import {loaderDisable, loaderEnable} from "../../ui/loaderView";
import {updateCanvasCount} from "../../../actions/actionFunctions";
import {setRoomIsSwitching} from "../../../actions/serverFunctions";
import {
  getCorrectedWizardGroupForFeature,
  getCurrentWizardNavItems,
  getFeatureGroupFromWizardGroup,
  getMissingWizardFeatures,
  shouldUseNewWizardFeaturesRequestsLogic
} from "../../look/lookNavigation/lookMain/lookWizard/lookWizardFunctions";
import type {WizardGroup, WizardGroupClickOnRoomLogic, WizardItem, WizardSelectedValue} from "../../../interfaces/Wizard";
import type {Feature, NavItem} from "../../../interfaces/Feature";
import type {BvPreset} from "../../../interfaces/BvPreset";
import {requestZoomToFeatureGroup} from "../../visualizerNew/zoomToFeatureGroup/featureZoomRequestStore";

type WindowWithRequestFlag = Window & { requestInProcess?: boolean };

let roomSwitchInProgress: Promise<void> | null = null; // prevents double switching on fast double clicks
let roomSwitchingCounter = 0; // nested / parallel runWhileRoomSwitching calls

/**
 * Keeps serverState.roomIsSwitching true while `fn` runs (materialsMainCanvas hides
 * NotInVisualizerView / NotCompatibleView meanwhile - the old active feature doesn't match the new room yet).
 * The flag is cleared on the next tick, so the caller's synchronous continuation after `await`
 * (e.g. SelectionMainList -> setActiveFeature with the new feature) runs before the views are allowed again.
 * @param fn {() => Promise<T>}
 * @returns {Promise<T>}
 */
export const runWhileRoomSwitching = async <T>(fn: () => Promise<T>): Promise<T> => {
  if (++roomSwitchingCounter === 1) {
    store.dispatch(setRoomIsSwitching(true));
  }
  try {
    return await fn()
  } finally {
    setTimeout(() => {
      roomSwitchingCounter = Math.max(0, roomSwitchingCounter - 1);
      if (!roomSwitchingCounter) {
        store.dispatch(setRoomIsSwitching(false));
      }
    }, 0);
  }
}

/**
 * Returns the preset described by `clickOnRoomLogic`:
 * the preset at `presetIndex` in `bvPresets` whose `layout_type` matches `clickOnRoomLogic.layout_type`.
 * @param clickOnRoomLogic {WizardGroupClickOnRoomLogic}
 * @returns {BvPreset | null}
 */
export const getClickOnRoomLogicPreset = (clickOnRoomLogic?: WizardGroupClickOnRoomLogic | null): BvPreset | null => {
  if (!clickOnRoomLogic) {
    return null
  }
  const {presetIndex, layout_type} = clickOnRoomLogic;
  if ((!presetIndex && presetIndex !== 0) || (!layout_type && layout_type !== 0)) {
    return null
  }
  const bvPresets: BvPreset[] = store.getState().bathroomVisualizerState.bvPresets;
  if (!Array.isArray(bvPresets)) {
    return null
  }
  return bvPresets.find((preset, index) => {
    return index === +presetIndex && +preset.layout_type === +layout_type
  }) || null
}

/**
 * Returns clickOnRoomLogic of the wizard group the feature really belongs to.
 * For merged groups getCorrectedWizardGroupForFeature returns a copy of the merged group with only the `id`
 * replaced (other props, clickOnRoomLogic too, stay from the merged group), so we take the real group by that id.
 * @param wizardGroup {WizardGroup}
 * @param feature {Feature} optional, without it (e.g. wizard group tile click) the passed group is used as is
 * @returns {WizardGroupClickOnRoomLogic | null}
 */
export const getWizardGroupClickOnRoomLogic = (wizardGroup?: WizardGroup | null,
                                               feature?: Feature | null): WizardGroupClickOnRoomLogic | null => {
  if (!wizardGroup) {
    return null
  }
  // getCorrectedWizardGroupForFeature runs on every click, so only resolve the real group when some merged group has clickOnRoomLogic
  if (feature && Array.isArray(wizardGroup.mergedGroups) && wizardGroup.mergedGroups.some(wizG => wizG.clickOnRoomLogic)) {
    const correctedWizardGroup: WizardGroup = getCorrectedWizardGroupForFeature(wizardGroup, feature);
    const realWizardGroup = wizardGroup.mergedGroups.find(wizG => wizG.id === correctedWizardGroup.id);
    if (realWizardGroup) {
      return realWizardGroup.clickOnRoomLogic || null
    }
  }
  return wizardGroup.clickOnRoomLogic || null
}

/**
 * Returns the preset to switch to, or null if the room should not be switched
 * (no clickOnRoomLogic, preset not found, or the current layout is already the needed one -
 * applying the preset again would reset everything the user selected so far).
 * @param wizardGroup {WizardGroup}
 * @param feature {Feature} clicked feature, needed to find the correct group for merged groups
 * @returns {BvPreset | null}
 */
export const shouldSwitchRoom = (wizardGroup?: WizardGroup | null, feature?: Feature | null): BvPreset | null => {
  const preset = getClickOnRoomLogicPreset(getWizardGroupClickOnRoomLogic(wizardGroup, feature));
  if (!preset) {
    return null
  }
  const layout = getCurrentLayout();
  if (!layout || +layout.type === +preset.layout_type) {
    return null
  }
  return preset
}

/**
 * same as Navigation -> handleDefaultPresetClick: kitchen cabinets have to be received before the preset is applied
 * @param layout {object} current layout
 */
const receiveMissingCabinets = async (layout) => {
  const cabinetParent = layout.parts["Cabinet"];
  const noCabinets = (cabinetParent && cabinetParent.topCabinet && !cabinetParent.topCabinet.allFeaturesReceived)
    || (cabinetParent && cabinetParent.bottomCabinet && !cabinetParent.bottomCabinet.allFeaturesReceived);
  if (!noCabinets || (document.kvConfig.visualizerType as string) === "kvScene") {
    return
  }
  const neededParts = ["top_cabinet", "bottom_cabinet"];
  const allRelated = cabinetParent.relatedItems.filter((relatedItem) => {
    return neededParts.includes(relatedItem.partId) && !relatedItem.allFeaturesReceived
  });
  await Promise.all(allRelated.map((relatedItem) => {
    // JSDoc of getBvGroupFeatures marks all params as required, the rest are optional in fact
    const request = getBvGroupFeatures as (groupName: string) => Promise<unknown>;
    return request(relatedItem.dbName || relatedItem.partName).then(response => {
      relatedItem.setGroupFeatures(Array.isArray(response) ? response : []);
      return response
    })
  }));
}

/**
 * Applies a room preset the same way Navigation -> handleDefaultPresetClick does, but can be awaited.
 * @param preset {BvPreset}
 */
export const switchRoomToPreset = async (preset: BvPreset): Promise<void> => {
  const layout = getCurrentLayout();
  const win = window as WindowWithRequestFlag;
  win.requestInProcess = true;
  loaderEnable();
  try {
    await receiveMissingCabinets(layout);
    await layout.setItemsFromBvState(preset);
    store.dispatch(updateCanvasCount());
  } catch (e) {
    console.error("switchRoomToPreset error", e);
  } finally {
    win.requestInProcess = false;
    loaderDisable();
  }
}

/**
 * Switches the room if wizardGroup.clickOnRoomLogic requires it. Resolves when the new room is applied.
 * @param wizardGroup {WizardGroup}
 * @param feature {Feature} clicked feature, needed to find the correct group for merged groups
 * @returns {Promise<boolean>} true if the room was switched
 */
export const switchRoomByWizardGroup = async (wizardGroup?: WizardGroup | null,
                                              feature?: Feature | null): Promise<boolean> => {
  if (roomSwitchInProgress) {
    await roomSwitchInProgress;
  }
  const preset = shouldSwitchRoom(wizardGroup, feature);
  if (!preset) {
    return false
  }
  if (Config.isDevMode()) {
    console.log("clickOnRoomLogic: switching room", getWizardGroupClickOnRoomLogic(wizardGroup, feature), preset);
  }
  roomSwitchInProgress = runWhileRoomSwitching(() => switchRoomToPreset(preset));
  try {
    await roomSwitchInProgress;
  } finally {
    roomSwitchInProgress = null;
  }
  return true
}

/**
 * true if the feature is bound to a RoomLayout which is not the current one
 * @param feature {Feature}
 */
export const isFeatureFromAnotherRoomLayout = (feature?: Feature | null): boolean => {
  const layout = getCurrentLayout();
  return !!(feature && feature.dbFeature && !feature.dbFeature.notAvailableInVisualizer
    && feature.dbFeature.metaData?.RoomLayout
    && !feature.matchesMetaForCondition("RoomLayout", layout.getCurrentLayoutAbbreviation()));
}

/**
 * sync check, so callers can skip awaiting anything in the common case
 * @param feature {Feature}
 * @param wizardGroup {WizardGroup}
 */
export const shouldResolveWizardFeatureBeforeClick = (feature?: Feature | null, wizardGroup?: WizardGroup | null): boolean => {
  return !!roomSwitchInProgress || !!shouldSwitchRoom(wizardGroup, feature)
    || isFeatureFromAnotherRoomLayout(feature)
}

/**
 * If the clicked feature belongs to another RoomLayout (e.g. the room was just switched by clickOnRoomLogic),
 * returns the same named (getSplittedFeatureName) feature from the current layout wizard nav items.
 * Returns the passed feature when nothing has to / can be replaced.
 */
export const getWizardFeatureForCurrentRoomLayout = async (feature: Feature,
                                                           wizardSelectedValues: Record<string, WizardSelectedValue>,
                                                           wizardActiveStep: number,
                                                           wizardActiveItem: WizardItem,
                                                           wizardGroup: WizardGroup): Promise<Feature> => {
  if (!isFeatureFromAnotherRoomLayout(feature) || !feature.getSplittedFeatureName) {
    return feature
  }
  const featureGroup = getFeatureGroupFromWizardGroup(wizardGroup);
  if (!featureGroup) {
    return feature
  }

  // same as materialsWizardMain -> setNavigationItems: the new layout features might not be received yet
  const layoutAbbr = getCurrentLayout().getCurrentLayoutAbbreviation();
  if (!featureGroup.allFeaturesReceived
    || (featureGroup.nextFeaturesPageToLoadArray
      && featureGroup.nextFeaturesPageToLoadArray[layoutAbbr]
      && !featureGroup.nextFeaturesPageToLoadArray[layoutAbbr].page)) {
    await checkIfLookFeaturesReceived(featureGroup, false);
  }

  const {filterGroups} = store.getState().filterState;
  const featureName = feature.getSplittedFeatureName();
  const findSameFeature = (navItems: NavItem[]): Feature | null => {
    const found = navItems.find(navItem => navItem.part && navItem.part !== feature
      && navItem.part.getSplittedFeatureName && navItem.part.getSplittedFeatureName() === featureName);
    return found ? found.part : null
  }

  let navItems: NavItem[] = getCurrentWizardNavItems(wizardSelectedValues, wizardActiveStep, wizardActiveItem,
    wizardGroup, featureGroup, filterGroups);
  let newFeature = findSameFeature(navItems);

  if (!newFeature && shouldUseNewWizardFeaturesRequestsLogic(wizardGroup, featureGroup)) {
    const shouldUpdateNavItems = await getMissingWizardFeatures(navItems as any, wizardGroup, featureGroup);
    if (shouldUpdateNavItems) {
      navItems = getCurrentWizardNavItems(wizardSelectedValues, wizardActiveStep, wizardActiveItem,
        wizardGroup, featureGroup, filterGroups);
      newFeature = findSameFeature(navItems);
    }
  }

  if (Config.isDevMode()) {
    console.log("getWizardFeatureForCurrentRoomLayout", {featureName, feature, newFeature});
  }
  return newFeature || feature
}

/**
 * After a clickOnRoomLogic room switch the docked canvas frames the wizard step's feature group
 * (useZoomToActiveFeatureGroup re-runs on the layout change). When the wizard group also has
 * clickOnLogic, the clicked clickOnLogic feature is what the user should see, so zoom to its feature
 * group instead. Call it after the feature is clicked (the hook reads the group's activeFeature).
 * @param wizardGroup {WizardGroup}
 * @param feature {Feature} the clickOnLogic feature that was clicked
 * @param roomSwitched {boolean} result of switchRoomByWizardGroup
 */
export const zoomToClickOnLogicFeatureAfterRoomSwitch = (wizardGroup: WizardGroup | null | undefined,
                                                         feature: Feature | null | undefined,
                                                         roomSwitched: boolean): void => {
  if (!roomSwitched || !feature || !wizardGroup?.clickOnLogic?.enabled) {
    return
  }
  const {partId} = feature; // the feature group (layout.getFeatureGroupByPartId) the hook frames
  if (Config.isDevMode()) {
    console.log("clickOnLogic: zooming to the clicked feature group after the room switch", partId, feature);
  }
  requestZoomToFeatureGroup(partId);
}
